import React, { useState } from 'react';
import { getAwsConfig, saveAwsConfig } from '../../services/pipelineProject.service';
import './Pipeline.css';

const AWS_REGIONS = [
  { value: 'us-east-1',      label: 'US East (N. Virginia) — us-east-1' },
  { value: 'us-east-2',      label: 'US East (Ohio) — us-east-2' },
  { value: 'us-west-1',      label: 'US West (N. California) — us-west-1' },
  { value: 'us-west-2',      label: 'US West (Oregon) — us-west-2' },
  { value: 'eu-west-1',      label: 'Europe (Ireland) — eu-west-1' },
  { value: 'eu-central-1',   label: 'Europe (Frankfurt) — eu-central-1' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore) — ap-southeast-1' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) — ap-northeast-1' },
  { value: 'ap-south-1',     label: 'Asia Pacific (Mumbai) — ap-south-1' },
];

const BUDGET_OPTIONS = [
  { value: 'minimal',  label: '🟢 Minimal', sub: 'under $50/month' },
  { value: 'moderate', label: '🟡 Moderate', sub: '$50–$500/month' },
  { value: 'high',     label: '🔴 Best', sub: 'no cost limit' },
];

const EMPTY = {
  accountId: '',
  region: 'us-east-1',
  budget: 'minimal',
  projectName: '',
  environment: 'dev',
  notificationEmail: '',
  existingVpcId: '',
  deployBucket: '',
};

/**
 * AWSOnboarding
 *
 * Collects required AWS context before the pipeline starts.
 * Saved to localStorage so it survives page refreshes.
 * Shows a summary card when already configured (with edit option).
 */
function AWSOnboarding({ onReady }) {
  const existing = getAwsConfig();
  const [editing, setEditing] = useState(!existing);
  const [form, setForm] = useState(existing || EMPTY);
  const [errors, setErrors] = useState({});

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    const e = {};
    if (!form.region)   e.region = 'Region is required';
    if (!form.budget)   e.budget = 'Budget is required';
    if (!form.projectName.trim()) e.projectName = 'Project name is required';
    if (form.notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.notificationEmail)) {
      e.notificationEmail = 'Invalid email address';
    }
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    saveAwsConfig(form);
    setEditing(false);
    onReady(form);
  };

  if (!editing && existing) {
    return (
      <div className="pipeline-card" style={{ margin: '0 0 20px', padding: '18px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.4rem' }}>☁️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>AWS Target Environment</div>
              <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                These settings are used by all pipeline stages
              </div>
            </div>
          </div>
          <button className="pipeline-btn secondary" style={{ fontSize: '0.8rem' }} onClick={() => setEditing(true)}>
            ✏️ Edit
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 20px', fontSize: '0.85rem' }}>
          {[
            ['Project', existing.projectName],
            ['Region', existing.region],
            ['Environment', existing.environment],
            ['Budget', BUDGET_OPTIONS.find(b => b.value === existing.budget)?.label],
            existing.accountId && ['AWS Account', `...${existing.accountId.slice(-4)}`],
            existing.notificationEmail && ['Notify', existing.notificationEmail],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k}>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
              <div style={{ fontWeight: 500, color: '#1e293b' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const field = (label, key, type = 'text', hint = '') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
        {label}{errors[key] && <span style={{ color: '#ef4444', fontWeight: 400, marginLeft: 6 }}>{errors[key]}</span>}
      </label>
      <input
        type={type}
        className={`pipeline-input${errors[key] ? ' error' : ''}`}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={hint}
      />
    </div>
  );

  return (
    <div className="pipeline-card" style={{ margin: '0 0 24px', padding: '22px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: '1.5rem' }}>☁️</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>AWS Environment Setup</div>
          <div style={{ fontSize: '0.83rem', color: '#64748b' }}>
            Collected once. Used to generate AWS-native solutions tailored to your account.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {field('Project Name *', 'projectName', 'text', 'my-task-manager')}
        {field('AWS Account ID', 'accountId', 'text', '123456789012 (optional)')}

        {/* Region */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            AWS Region *{errors.region && <span style={{ color: '#ef4444', fontWeight: 400, marginLeft: 6 }}>{errors.region}</span>}
          </label>
          <select className="pipeline-input" value={form.region} onChange={e => set('region', e.target.value)}>
            {AWS_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        {/* Environment */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Environment</label>
          <select className="pipeline-input" value={form.environment} onChange={e => set('environment', e.target.value)}>
            <option value="dev">Development</option>
            <option value="staging">Staging</option>
            <option value="prod">Production</option>
          </select>
        </div>

        {/* Budget */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            Budget Preference *{errors.budget && <span style={{ color: '#ef4444', fontWeight: 400, marginLeft: 6 }}>{errors.budget}</span>}
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {BUDGET_OPTIONS.map(b => (
              <button
                key={b.value}
                type="button"
                className={`pipeline-btn ${form.budget === b.value ? 'primary' : 'secondary'}`}
                style={{ fontSize: '0.83rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, padding: '8px 14px' }}
                onClick={() => set('budget', b.value)}
              >
                <span>{b.label}</span>
                <span style={{ fontSize: '0.72rem', opacity: 0.75, fontWeight: 400 }}>{b.sub}</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>
            All 3 solutions are always generated. Your preference pre-selects one, but you can pick any.
          </div>
        </div>

        {field('Notification Email', 'notificationEmail', 'email', 'you@example.com (optional)')}
        {field('Existing VPC ID', 'existingVpcId', 'text', 'vpc-xxxxxxxx (optional)')}
        {field('S3 Deploy Bucket', 'deployBucket', 'text', 'my-deploy-bucket (optional)')}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button className="pipeline-btn primary" onClick={handleSave}>
          ✅ Save & Start Pipeline
        </button>
        {existing && (
          <button className="pipeline-btn secondary" onClick={() => setEditing(false)}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export default AWSOnboarding;
